/**
 * A letra de cada arquivo na aba Documentos (E216.F3, GUS-317 item 5): `GET
 * /casos/{id}/documentos/letras` devolve a letra que a seção 2 do exame imprime, pelo SHA-256 de
 * custódia. O arquivo mantido que a seção 2 não lista e o excluído ficam sem letra ("—"). Sem a
 * resposta (o exame ainda não composto, a falha da rede), a aba segue sem letras.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

export interface LetraDoArquivoData {
  sha256: string;
  letra: string;
}

export interface LetrasDaSecao2Data {
  letras: readonly LetraDoArquivoData[];
}

/** O mapa SHA-256 → letra da resposta. */
export function mapaDasLetras(dados: LetrasDaSecao2Data): ReadonlyMap<string, string> {
  return new Map(dados.letras.map((l) => [l.sha256, l.letra]));
}

export async function lerLetrasDaSecao2(
  client: AxiosInstance,
  caseId: string,
): Promise<ReadonlyMap<string, string>> {
  const { data } = await client.get<LetrasDaSecao2Data>(`/casos/${caseId}/documentos/letras`);
  return mapaDasLetras(data);
}

const VAZIO: ReadonlyMap<string, string> = new Map();

/** As letras do caso, relidas quando `releitura` muda; vazio enquanto lê ou se falhar. */
export function useLetrasDaSecao2(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): ReadonlyMap<string, string> {
  const [letras, setLetras] = useState<ReadonlyMap<string, string>>(VAZIO);
  useEffect(() => {
    let vivo = true;
    lerLetrasDaSecao2(client, caseId)
      .then((m) => {
        if (vivo) setLetras(m);
      })
      .catch(() => {
        if (vivo) setLetras(VAZIO);
      });
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura]);
  return letras;
}
